import { useState, useEffect } from 'react';
import { eventsAPI } from '../api';
import './CreateEventModal.css';

function CreateEventModal({ isOpen, onClose, onEventCreated }) {
  const [name, setName] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [sendThankYouSms, setSendThankYouSms] = useState(true);
  const [watermarkFile, setWatermarkFile] = useState(null);
  const [watermarkPreview, setWatermarkPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  // Check if user is super admin and fetch clients
  useEffect(() => {
    const adminData = JSON.parse(localStorage.getItem('user') || '{}');
    const isSuperAdminUser = adminData.role === 'super_admin';
    setIsSuperAdmin(isSuperAdminUser);

    if (isSuperAdminUser && isOpen) {
      fetchClients();
    }
  }, [isOpen]);

  const fetchClients = async () => {
    try {
      const token = sessionStorage.getItem('adminToken') || localStorage.getItem('token');
      const response = await fetch('https://live-event-photos.onrender.com/api/admin/clients', {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      const data = await response.json();
      setClients(data.clients || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const handleWatermarkChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/png')) {
        alert('Please upload a PNG file');
        return;
      }
      
      // Validate file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        alert('Watermark file must be less than 2MB');
        return;
      }
      
      setWatermarkFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setWatermarkPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  const removeWatermark = () => {
    setWatermarkFile(null);
    setWatermarkPreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name || !startTime || !endTime) {
      alert('Please fill in all fields');
      return;
    }

    try {
      setLoading(true);
      
      // Upload watermark if provided
      let watermarkUrl = null;
      if (watermarkFile) {
        const formData = new FormData();
        formData.append('watermark', watermarkFile);
        
        try {
          const uploadResponse = await eventsAPI.uploadWatermark(formData);
          watermarkUrl = uploadResponse.data.url;
        } catch (uploadError) {
          console.error('Error uploading watermark:', uploadError);
          alert('Failed to upload watermark. Creating event without watermark.');
        }
      }
      
      const eventData = {
        name,
        start_time: new Date(startTime).getTime(),
        end_time: new Date(endTime).getTime(),
        send_thank_you_sms: sendThankYouSms,
        watermark_url: watermarkUrl,
      };

      // If super admin and client selected, add venue_id
      if (isSuperAdmin && selectedClient) {
        eventData.venue_id = selectedClient;
      }
      
      const response = await eventsAPI.create(eventData);
      
      // Reset form
      setName('');
      setStartTime('');
      setEndTime('');
      setSendThankYouSms(true);
      setWatermarkFile(null);
      setWatermarkPreview(null);
      
      // Notify parent and close
      if (onEventCreated) onEventCreated(response.data);
      onClose();
    } catch (error) {
      console.error('Error creating event:', error);
      alert(`Failed to create event: ${error.response?.data?.error || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content create-event-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Create New Event</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="modal-body">
          <div className="form-group">
            <label className="form-label">Event Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Summer Wedding"
              className="form-input"
            />
          </div>

          {isSuperAdmin && (
            <div className="form-group">
              <label className="form-label">Client (Optional)</label>
              <select
                value={selectedClient}
                onChange={(e) => setSelectedClient(e.target.value)}
                className="form-input"
              >
                <option value="">Select a client (or create for yourself)</option>
                {clients.map(client => (
                  <option key={client.id} value={client.id}>
                    {client.first_name} {client.last_name} ({client.email})
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Start Time</label>
            <input
              type="datetime-local"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label className="form-label">End Time</label>
            <input
              type="datetime-local"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Watermark (Optional)</label>
            <p className="form-help">
              Upload a PNG logo to appear at the bottom center of photos sent via MMS
            </p>
            
            <label className="file-upload-button">
              <span>Choose PNG File</span>
              <input
                type="file"
                accept="image/png"
                onChange={handleWatermarkChange}
                style={{ display: 'none' }}
              />
            </label>
            
            {watermarkPreview && (
              <div className="file-uploaded">
                <span className="checkmark">✓</span>
                <span className="filename">{watermarkFile?.name || 'Watermark uploaded'}</span>
                <button 
                  type="button"
                  onClick={removeWatermark}
                  className="btn-remove"
                >
                  ×
                </button>
              </div>
            )}
          </div>

          <div className="form-group">
            <label className="checkbox-inline">
              <input
                type="checkbox"
                checked={sendThankYouSms}
                onChange={(e) => setSendThankYouSms(e.target.checked)}
                className="checkbox-input"
              />
              <span className="checkbox-text">Send Thank You SMS</span>
            </label>
            <p className="form-help" style={{ marginTop: '4px' }}>
              Automatically send attendees a thank you message with their photo gallery link when the event ends
            </p>
          </div>

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Creating...' : 'Create Event'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CreateEventModal;
